import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSliderModule } from '@angular/material/slider';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { isDefined, isNil } from '@trimble-gcs/common';
import { TranslationService } from '@trimble-gcs/i18n';
import { ModusButtonModule, ModusIconModule, ModusTooltipModule } from '@trimble-gcs/modus';
import { FileSizePipe } from '@trimble-gcs/ngx-common';
import { filter, switchMap } from 'rxjs';
import { AppState } from '../../app-state/app.state';
import { ConnectProjectService } from '../../connect/connect-project.service';
import { LoadingService } from '../../loading/loading.service';
import { PercentagePipe } from '../../pipes/percentage.pipe';
import { ProjectQuota } from '../../quota/project-quota.models';
import { ProjectQuotaService } from '../../quota/project-quota.service';
import { ProjectUsageTableComponent } from './project-usage-table/project-usage-table.component';
import { storageTranslations } from './storage.translations';
import { UsageBarComponent } from './usage-bar/usage-bar.component';

const QUOTA_WARNING_THRESHOLD = 80; // Usage percentage above this value will show a warning message

interface ProjectQuotaView extends ProjectQuota {
  quotaUsedByAccountPercent: number;
  quotaUsedByAccountDisplayValue: string;
  quotaUsedByProjectPercent: number;
  quotaUsedByProjectDisplayValue: string;
}

@UntilDestroy()
@Component({
  selector: 'sd-config-storage',
  imports: [
    CommonModule,
    UsageBarComponent,
    ModusButtonModule,
    ModusIconModule,
    ModusTooltipModule,
    MatProgressBarModule,
    MatSliderModule,
    FileSizePipe,
    ProjectUsageTableComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './storage.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StorageComponent {
  private readonly store = inject(Store);
  private readonly loadingService = inject(LoadingService);
  private readonly connectProjectService = inject(ConnectProjectService);
  private readonly projectQuotaService = inject(ProjectQuotaService);
  private readonly translationService = inject(TranslationService);

  readonly translations = storageTranslations();
  private readonly fileSizePipe = new FileSizePipe();
  private readonly percentagePipe = new PercentagePipe();

  readonly title = computed(() =>
    this.translationService.translate(this.translations.title.text, {
      'Reality Capture': 'Reality Capture',
    }),
  );

  readonly isLoading = toSignal(this.loadingService.isLoading$(this), { requireSync: true });

  readonly primaryAccount = toSignal(this.getPrimaryAccount());

  readonly projectQuotaView = computed(() => {
    const projectQuota = this.projectQuota();
    return isDefined(projectQuota) ? this.getProjectQuotaView(projectQuota) : undefined;
  });

  private readonly projectQuota = toSignal(this.getProjectQuota());
  private readonly appSettings = this.store.selectSignal(AppState.settings);
  readonly connectUrl = computed(() => this.appSettings().connectUrl);

  // storage message
  readonly storageMessageType = computed<'warning' | 'error' | null>(() => {
    const usage = this.projectQuotaView()?.quotaUsedByAccountPercent;
    if (isNil(usage)) return null;

    if (usage >= 100) return 'error';
    if (usage >= QUOTA_WARNING_THRESHOLD) return 'warning';
    return null;
  });

  readonly showStorageMessage = computed(() => isDefined(this.storageMessageType()));

  readonly storageMessage = computed(() => {
    const type = this.storageMessageType();
    if (isNil(type)) return null;

    const quota = this.projectQuotaView()?.quota ?? 0;
    const quotaUsed = this.projectQuotaView()?.quotaUsedByAccount ?? 0;

    if (type === 'error') {
      const over = this.fileSizePipe.transform(quotaUsed - quota);
      const text = this.translations.storageUsed.limitReached.text;
      return this.translationService.translate(text, { value: over });
    } else {
      const remaining = this.fileSizePipe.transform(quota - quotaUsed);
      const text = this.translations.storageUsed.limitWarning.text;
      return this.translationService.translate(text, { value: remaining });
    }
  });

  readonly storageUsedBackgroundColor = computed(() => {
    const storageMessageType = this.storageMessageType();

    let className = 'bg-green-light';
    if (storageMessageType === 'error') {
      className = 'bg-red';
    } else if (storageMessageType === 'warning') {
      className = 'bg-warning';
    }

    return className;
  });

  private getPrimaryAccount() {
    return this.store.select(AppState.project).pipe(
      filter(isDefined),
      switchMap((project) => this.connectProjectService.getPrimaryAccount(project)),
    );
  }

  private getProjectQuota() {
    return this.loadingService.loadFrom(this.projectQuotaService.getProjectQuota(), this);
  }

  private getProjectQuotaView(projectQuota: ProjectQuota) {
    const quotaUsedByAccountPercent =
      Math.abs(projectQuota.quota) > 0
        ? (projectQuota.quotaUsedByAccount / projectQuota.quota) * 100
        : 100;

    const quotaUsedByAccountDisplayValue =
      quotaUsedByAccountPercent > 100
        ? '> 100%'
        : this.percentagePipe.transform(quotaUsedByAccountPercent, 0);

    const quotaUsedByProjectPercent =
      Math.abs(projectQuota.quota) > 0
        ? (projectQuota.quotaUsedByProject / projectQuota.quota) * 100
        : 100;

    const quotaUsedByProjectDisplayValue =
      quotaUsedByProjectPercent > 100
        ? '> 100%'
        : this.percentagePipe.transform(quotaUsedByProjectPercent, 0);

    return {
      ...projectQuota,
      quotaUsedByAccountPercent,
      quotaUsedByAccountDisplayValue,
      quotaUsedByProjectPercent,
      quotaUsedByProjectDisplayValue,
    } satisfies ProjectQuotaView;
  }
}
